<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">إدارة الصفحة الرئيسية</h1>
          <p class="ds-page-header__subtitle">تحكم كامل في محتوى وترتيب أقسام الصفحة الرئيسية مع معاينة فورية</p>
        </div>
        <a :href="publicSiteUrl" target="_blank" class="ds-btn ds-btn--secondary">
          <i class="pi pi-external-link"></i> معاينة الموقع
        </a>
      </div>

      <div class="hb-layout" dir="rtl">
        <div class="hb-layout__sections">
          <SectionsList :selected-key="selectedKey" @edit="openEditor" />
        </div>
        <div class="hb-layout__preview">
          <PreviewPanel :sections="store.sections" :selected-key="selectedKey" @section-click="onPreviewSectionClick" />
        </div>
      </div>
    </div>
  </div>

  <!-- Section-specific editor — chosen per `type` (per `kind` for the two
       home_products rows) via the editor registry, never one giant generic
       form. -->
  <component
    :is="activeEditor"
    v-if="activeSection"
    :section="activeSection"
    @close="activeSection = null"
    @saved="onSaved"
  />
</template>

<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import SectionsList from './SectionsList.vue';
import PreviewPanel from './preview/PreviewPanel.vue';
import { editorRegistry } from './editors/editorRegistry.js';
import { useHomepageSectionStore } from '@/stores/useHomepageSectionStore.js';

export default {
  name: 'HomepageBuilderIndex',
  components: { Breadcrumb, SectionsList, PreviewPanel },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'إدارة الصفحة الرئيسية', path: '/homepage-builder' }],
      activeSection: null,
    };
  },
  computed: {
    store() { return useHomepageSectionStore(); },
    publicSiteUrl() {
      // Mirrors the existing api_base_url localStorage-override convention
      // (main.js) — this project has no .env/VUE_APP_* mechanism anywhere,
      // so a hardcoded production default + local override is consistent
      // rather than introducing a new config pattern for just this feature.
      // Same key PreviewPanel.vue reads independently (duplicated as-is,
      // matching the reference implementation rather than centralizing it).
      return localStorage.getItem('homepage_preview_origin') || 'https://tarekmath.com';
    },
    selectedKey() {
      return this.activeSection?.key ?? null;
    },
    activeEditor() {
      if (!this.activeSection) return null;
      // home_products is the one type used by two different rows
      // (academic_years_products / courses_products, disambiguated by
      // settings.kind) — everything else is a plain type -> editor lookup.
      if (this.activeSection.type === 'home_products') {
        return this.activeSection.settings?.kind === 'course'
          ? editorRegistry.courses
          : editorRegistry.academic_years;
      }
      return editorRegistry[this.activeSection.type] || null;
    },
  },
  methods: {
    openEditor(section) {
      this.activeSection = section;
    },
    onSaved() {
      this.activeSection = null;
    },
    // Clicking a section directly inside the live preview (section-click
    // postMessage, bubbled up by PreviewPanel) selects/opens it here too —
    // same "selection" concept the section list highlights.
    onPreviewSectionClick(key) {
      const section = this.store.sections.find((s) => s.key === key);
      if (section) this.openEditor(section);
    },
  },
  mounted() {
    this.store.fetchSections();
  },
};
</script>

<style scoped>
.hb-layout {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  margin-top: 1rem;
}
.hb-layout__sections {
  flex: 1;
  min-width: 0;
}
.hb-layout__preview {
  flex: 1;
  min-width: 0;
  position: sticky;
  top: 1rem;
}

@media (max-width: 991.98px) {
  .hb-layout {
    flex-direction: column;
  }
  .hb-layout__preview {
    position: static;
    order: -1;
  }
}
</style>
